import clsx from "clsx";
import { memo, useCallback, useEffect, useId, useRef, useState } from "react";
import type { LocalStateUI } from "../../../../../common/types.ts";
import i18n from "../../util/i18n.ts";
import { useLocalPartial, localActions } from "../../util/local.ts";
import { ScoreBox } from "../ScoreBox/index.tsx";
import { emitter } from "../Modal.tsx";
import { MobileTooltip } from "../MobileTooltip.tsx";
import type { LeagueChromeScoreMode } from "./chromePolicy.ts";
import { translateTeamShortName } from "../../util/teamNames.ts";
import { getLeagueTopBarGameState } from "../../util/liveGameStatus.ts";

const Toggle = ({ show, toggle }: { show: boolean; toggle: () => void }) => {
	const label = show
		? i18n.t("leagueTopBar.hideScores", {
				ns: "ui",
				defaultValue: "Hide scores",
			})
		: i18n.t("leagueTopBar.showScores", {
				ns: "ui",
				defaultValue: "Show scores",
			});

	// container-fluid is needed to make this account for scrollbar width when modal is open
	return (
		<MobileTooltip title={label}>
			<button
				aria-label={label}
				className="btn btn-secondary p-0 league-top-bar-toggle"
				onClick={toggle}
			>
				<span
					className={clsx(
						"glyphicon",
						show ? "glyphicon-menu-right" : "glyphicon-menu-left",
					)}
				/>
			</button>
		</MobileTooltip>
	);
};

const hiddenStyle = {
	height: 0,
};

const IS_SAFARI = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

type ScoreTeam = LocalStateUI["games"][number]["teams"][number];
type TeamInfoCache = LocalStateUI["teamInfoCache"];

const getTeamLabel = (team: ScoreTeam, teamInfoCache: TeamInfoCache) => {
	const teamInfo = team.tid >= 0 ? teamInfoCache[team.tid] : undefined;

	if (teamInfo) {
		return translateTeamShortName(teamInfo, teamInfo.abbrev);
	}

	return String(team.tid);
};

export const LeagueTopBar = memo(
	({ scoreMode }: { scoreMode: LeagueChromeScoreMode }) => {
		const {
			games,
			lid,
			liveGameInProgress,
			liveGameStatus,
			showLeagueTopBar,
			teamInfoCache,
		} =
			useLocalPartial([
				"games",
				"lid",
				"liveGameInProgress",
				"liveGameStatus",
				"showLeagueTopBar",
				"teamInfoCache",
			]);

		const keepScrollToRightRef = useRef(true);
		const [compactExpanded, setCompactExpanded] = useState(false);
		const compactTrayId = `league-top-bar-compact-tray-${useId().replaceAll(":", "")}`;

		const [wrapperElement, setWrapperElement] =
			useState<HTMLDivElement | null>(null);

		const prevGames = useRef<typeof games>([]);

		const games2: typeof games = [];

		const keepScrolledToRightIfNecessary = useCallback(() => {
			if (
				keepScrollToRightRef.current &&
				wrapperElement &&
				wrapperElement.scrollLeft + wrapperElement.offsetWidth <
					wrapperElement.scrollWidth
			) {
				wrapperElement.scrollTo({
					left: wrapperElement.scrollWidth,
				});
			}
		}, [wrapperElement]);

		useEffect(() => {
			return emitter.on("keepScrollToRight", keepScrolledToRightIfNecessary);
		}, [keepScrolledToRightIfNecessary]);

		useEffect(() => {
			if (scoreMode === "expanded") {
				setCompactExpanded(false);
			}
		}, [scoreMode]);

		useEffect(() => {
			if (
				!wrapperElement ||
				(scoreMode === "expanded" ? !showLeagueTopBar : !compactExpanded)
			) {
				return;
			}

			const handleWheel = (event: WheelEvent) => {
				if (
					!wrapperElement ||
					wrapperElement.scrollWidth <= wrapperElement.clientWidth ||
					event.altKey ||
					event.ctrlKey ||
					event.metaKey ||
					event.shiftKey
				) {
					return;
				}

				// We're scrolling within the bar, not within the whole page
				event.preventDefault();

				const leagueTopBarPosition = wrapperElement.scrollLeft;

				wrapperElement.scrollTo({
					// Normal mouse wheels are just deltaY, but trackpads (such as on Mac) can include both, and I think there's no way to tell if this event came from a device supporting two dimensional scrolling or not.
					left: leagueTopBarPosition + 2 * (event.deltaX + event.deltaY),
				});
			};

			// This triggers for wheel scrolling and click scrolling
			const handleScroll = () => {
				if (
					!wrapperElement ||
					wrapperElement.scrollWidth <= wrapperElement.clientWidth
				) {
					return;
				}

				// Keep track of if we're scrolled to the right or not
				const FUDGE_FACTOR = 50; // Off by a few pixels? That's fine!
				keepScrollToRightRef.current =
					wrapperElement.scrollLeft + wrapperElement.offsetWidth >=
					wrapperElement.scrollWidth - FUDGE_FACTOR;
			};

			wrapperElement.addEventListener("wheel", handleWheel, { passive: false });
			wrapperElement.addEventListener("scroll", handleScroll, {
				passive: false,
			});

			// This works better than the global "resize" event because it also handles when the div size changes due to other reasons, like the window's scrollbar appearing or disappearing
			const resizeObserver = new ResizeObserver(keepScrolledToRightIfNecessary);
			resizeObserver.observe(wrapperElement);

			return () => {
				wrapperElement.removeEventListener("wheel", handleWheel);
				wrapperElement.removeEventListener("scroll", handleScroll);
				resizeObserver.disconnect();
			};
		}, [
			compactExpanded,
			keepScrolledToRightIfNecessary,
			scoreMode,
			showLeagueTopBar,
			wrapperElement,
		]);

		if (scoreMode === "hidden") {
			return null;
		}

		// If you take control of an expansion team after the season, the ASG is the only game, and it looks weird to show just it
		const onlyAllStarGame =
			games.length === 1 &&
			games[0]!.teams[0].tid === -1 &&
			games[0]!.teams[1].tid === -2;

		if (lid === undefined || games.length === 0 || onlyAllStarGame) {
			return <div className="mt-2" />;
		}

		// Don't show any new games if liveGameInProgress
		if (!liveGameInProgress) {
			prevGames.current = games;
		}

		const showScoreBoxes =
			scoreMode === "expanded" ? showLeagueTopBar : compactExpanded;

		if (showScoreBoxes) {
			// Show only the first upcoming game
			for (const game of prevGames.current) {
				games2.push(game);
				if (game.teams[0].pts === undefined) {
					break;
				}
			}
		}

		// In a new season, start scrolled to right
		if (games2.length <= 1) {
			keepScrollToRightRef.current = true;
		}

		// Keep scrolled to the right, if something besides a scroll event has moved us away (i.e. a game was simmed and added to the list)
		keepScrolledToRightIfNecessary();

		if (scoreMode === "compact") {
			const gameForSummary = liveGameInProgress
				? prevGames.current.find((game) => game.gid === liveGameStatus?.gid) ??
					games.find((game) => game.gid === liveGameStatus?.gid) ??
					prevGames.current.find((game) => game.teams[0].pts === undefined)
				: prevGames.current.find((game) => game.teams[0].pts === undefined) ??
					prevGames.current.at(-1);
			const gameState = gameForSummary
				? getLeagueTopBarGameState({
						game: gameForSummary,
						liveGameInProgress,
						liveGameStatus,
					})
				: undefined;
			const statusLabel = i18n.t(
				gameState?.kind === "live"
					? "leagueTopBar.live"
					: gameState?.kind === "halftime"
						? "leagueTopBar.halftime"
						: gameState?.kind === "final"
							? "leagueTopBar.final"
							: "leagueTopBar.nextGame",
				{
					ns: "ui",
					defaultValue:
						gameState?.kind === "live"
							? "Live"
							: gameState?.kind === "halftime"
								? "Halftime"
								: gameState?.kind === "final"
									? "Final"
									: "Next",
				},
			);
			const matchupSummary = gameForSummary
				? gameState?.awayPts !== undefined && gameState.homePts !== undefined
					? i18n.t("leagueTopBar.scoreLine", {
							ns: "ui",
							defaultValue: "{{away}} {{awayScore}}–{{homeScore}} {{home}}",
							away: getTeamLabel(gameForSummary.teams[0], teamInfoCache),
							awayScore: gameState.awayPts,
							home: getTeamLabel(gameForSummary.teams[1], teamInfoCache),
							homeScore: gameState.homePts,
						})
					: i18n.t("leagueTopBar.scoreSummary", {
							ns: "ui",
							defaultValue: "{{away}} at {{home}}",
							away: getTeamLabel(gameForSummary.teams[0], teamInfoCache),
							home: getTeamLabel(gameForSummary.teams[1], teamInfoCache),
						})
				: i18n.t("leagueTopBar.scores", {
						ns: "ui",
						defaultValue: "Scores",
					});
			let liveContext: string | undefined;
			if (gameState?.kind === "live") {
				if (gameState.target !== undefined) {
					liveContext = i18n.t("leagueTopBar.target", {
						ns: "ui",
						defaultValue: "Target {{target}}",
						target: gameState.target,
					});
				} else if (gameState.period !== undefined) {
					const period =
						gameState.numPeriods !== undefined &&
						gameState.period > gameState.numPeriods
							? i18n.t("leagueTopBar.overtime", {
									ns: "ui",
									defaultValue: "OT{{overtime}}",
									overtime: gameState.period - gameState.numPeriods,
								})
							: i18n.t("leagueTopBar.period", {
									ns: "ui",
									defaultValue: "Q{{period}}",
									period: gameState.period,
								});
					liveContext = gameState.time ? `${period} ${gameState.time}` : period;
				}
			}
			const scoreSummary = liveContext
				? `${liveContext} · ${matchupSummary}`
				: matchupSummary;

			return (
				<div
					className="league-top-bar-compact flex-shrink-0 mt-2 px-2"
					data-league-top-bar-compact-state={compactExpanded ? "expanded" : "collapsed"}
					data-league-top-bar-mode="compact"
				>
					<button
						aria-controls={compactTrayId}
						aria-expanded={compactExpanded}
						className="btn btn-light-bordered btn-sm league-top-bar-compact-toggle"
						data-league-top-bar-compact-toggle=""
						onClick={() => setCompactExpanded((value) => !value)}
						type="button"
					>
						<span
							className={clsx({
								"league-top-bar-compact-status-live":
									gameState?.kind === "live" || gameState?.kind === "halftime",
							})}
							data-league-top-bar-game-status={gameState?.kind ?? "empty"}
						>
							{statusLabel}
						</span>
						<span
							className="text-body-secondary text-truncate"
							data-league-top-bar-compact-summary=""
						>
							{scoreSummary}
						</span>
					</button>
					{compactExpanded ? (
						<div
							aria-label={i18n.t("leagueTopBar.scores", {
								ns: "ui",
								defaultValue: "Scores",
							})}
							className="league-top-bar-compact-tray d-flex overflow-auto small-scrollbar flex-row pt-2"
							data-league-top-bar-compact-tray=""
							id={compactTrayId}
							ref={(element) => {
								setWrapperElement(element);
							}}
							role="region"
						>
							{gameState?.kind === "live" || gameState?.kind === "halftime" ? (
								<div
									className="league-top-bar-live-detail w-100"
									data-league-top-bar-live-detail=""
								>
									<strong>{statusLabel}</strong>
									<span className="text-body-secondary">{scoreSummary}</span>
								</div>
							) : (
								games2.map((game) => (
									<ScoreBox className="me-2" game={game} key={game.gid} small />
								))
							)}
						</div>
					) : null}
				</div>
			);
		}

		return (
			<div
				className={`league-top-bar${
					IS_SAFARI ? " league-top-bar-safari" : ""
				} flex-shrink-0 d-flex overflow-auto small-scrollbar flex-row ps-1 mt-2`}
				style={showScoreBoxes ? undefined : hiddenStyle}
				ref={(element) => {
					// Shit is wild, if I just do ref={setWrapperElement} it somehow breaks scrolling to the right, idk why
					setWrapperElement(element);
				}}
			>
				<Toggle
					show={showScoreBoxes}
					toggle={() => {
						if (showLeagueTopBar === false) {
							// When showing, always scroll to right
							keepScrollToRightRef.current = true;
						}
						localActions.setShowLeagueTopBar(!showLeagueTopBar);
					}}
				/>
				{showScoreBoxes
					? games2.map((game, i) => (
							<ScoreBox
								key={game.gid}
								className={`me-2${i === 0 ? " ms-auto" : ""}`}
								game={game}
								small
							/>
						))
					: null}
			</div>
		);
	},
);
